import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, ArrowRight } from 'lucide-react';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { getRegion } from '@/lib/api';

const RegionDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [region, setRegion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await getRegion(slug);
        setRegion(res.data);
        // SEO metadata — per-region title & description
        const name = res.data.name;
        document.title = `Bali Real Estate in ${name} | Lalita Properties`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
          meta.setAttribute(
            'content',
            res.data.description ||
              `Browse villas, land, and rentals in ${name}, Bali. ${res.data.total} live listings on Lalita Properties.`
          );
        }
        // Inject JSON-LD structured data for rich search results
        const existing = document.getElementById('region-jsonld');
        if (existing) existing.remove();
        const script = document.createElement('script');
        script.id = 'region-jsonld';
        script.type = 'application/ld+json';
        script.text = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Place',
          name: `${name}, Bali`,
          description: res.data.description,
          containedInPlace: { '@type': 'AdministrativeArea', name: 'Bali, Indonesia' },
        });
        document.head.appendChild(script);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        if (process.env.NODE_ENV !== 'production') console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center pt-20">
        <div className="text-center px-4">
          <h1 className="font-heading text-3xl text-charcoal mb-3">Region not found</h1>
          <p className="text-charcoal-muted mb-6">
            We couldn&apos;t find a region matching <code className="bg-warm-sand-dark px-1 rounded">{slug}</code>.
          </p>
          <Button onClick={() => navigate('/regions')} data-testid="back-to-regions">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to all regions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-warm-sand min-h-screen" data-testid="region-detail-page">
      {/* Hero */}
      <section className="bg-ubud-jungle text-white pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/regions"
            className="inline-flex items-center gap-1 text-white/70 hover:text-temple-gold text-sm mb-6 transition-colors"
            data-testid="region-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            All regions
          </Link>
          {loading || !region ? (
            <div className="space-y-4">
              <div className="h-4 w-24 bg-white/10 rounded animate-pulse" />
              <div className="h-12 w-64 bg-white/10 rounded animate-pulse" />
              <div className="h-4 w-96 bg-white/10 rounded animate-pulse" />
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-temple-gold" />
                <span className="text-xs text-temple-gold uppercase tracking-[0.25em]">Bali, Indonesia</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light mb-2 leading-tight">
                {region.name}
              </h1>
              {region.tagline && (
                <p className="font-body italic text-white/80 text-lg mb-5">{region.tagline}</p>
              )}
              {region.description && (
                <p className="font-body text-white/75 max-w-3xl leading-relaxed">{region.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-6 mt-6 text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-temple-gold" />
                  {region.for_sale} for sale
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-white/40" />
                  {region.for_rent_long} long-term rentals
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-300" />
                  {region.for_rent_short} short stays
                </span>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Sub-locations */}
      {region && region.sub_locations.length > 0 && (
        <section className="bg-white border-y border-gray-100 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-[11px] uppercase tracking-[0.25em] text-charcoal-muted mb-3">
              Sub-areas in {region.name}
            </p>
            <div className="flex flex-wrap gap-2">
              {region.sub_locations.map((loc) => {
                // Sub-areas with a dedicated landing page get a nested URL;
                // others fall back to the filtered properties listing.
                const subSlug = loc.toLowerCase().replace(/\s+/g, '-');
                // Keep this list in sync with backend SUB_AREA_META.
                const SUB_AREA_SLUGS = new Set([
                  'pererenan', 'berawa', 'echo-beach', 'batu-bolong',
                  'bingin', 'padang-padang',
                ]);
                const href = SUB_AREA_SLUGS.has(subSlug)
                  ? `/region/${region.slug}/${subSlug}`
                  : `/properties?location=${encodeURIComponent(loc)}`;
                return (
                  <Link
                    key={loc}
                    to={href}
                    className="text-xs bg-warm-sand text-charcoal hover:bg-temple-gold hover:text-white px-3 py-1.5 rounded-full transition-colors"
                    data-testid={`sub-location-${subSlug}`}
                  >
                    {loc}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Property grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : region && region.properties.length > 0 ? (
            <>
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="font-heading text-2xl text-charcoal">
                  Latest listings in {region.name}
                </h2>
                {region.total > region.properties.length && (
                  <Link
                    to={`/properties?location=${encodeURIComponent(region.sub_locations[0] || region.name)}`}
                    className="text-sm text-ubud-jungle hover:underline flex items-center gap-1"
                    data-testid="region-see-all"
                  >
                    See all {region.total} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {region.properties.map((p) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <p className="font-heading text-2xl text-charcoal mb-2">
                Nothing live in {region?.name} yet
              </p>
              <p className="text-charcoal-muted mb-6 max-w-md mx-auto">
                We&apos;re actively sourcing here. Tell us what you&apos;re looking for and we&apos;ll reach out as soon as inventory arrives.
              </p>
              <Button asChild>
                <Link to="/contact" data-testid="region-empty-contact">Get in touch</Link>
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RegionDetailPage;
